import { key, subscribeTo } from '@databases/ping-registry';
import { useCallback, useEffect, useState } from 'react';

import { presenceState } from '../presence-state-instance';
import { type Participant } from '../types';
import { type FieldEntryGrouping } from '../types';
import {
  areArraysShallowEqual,
  isCellGrouping,
  isColumnGrouping,
  isRowGrouping
} from '../utils';

export const usePresence = ({
  grouping,
  componentId,
  isEnabled
}: {
  grouping?: FieldEntryGrouping;
  componentId: string;
  isEnabled: boolean;
}) => {
  const getCollaboratorColor = useCallback((grouping: FieldEntryGrouping) => {
    return presenceState.getRecentCollaboratorColor(grouping);
  }, []);

  const getParticipants = useCallback((grouping: FieldEntryGrouping) => {
    return presenceState.getParticipantsFromGrouping(grouping);
  }, []);

  const [participants, setParticipants] = useState<Participant[]>(
    grouping ? getParticipants(grouping) : []
  );
  const [recentCollaboratorColor, setRecentCollaboratorColor] = useState<
    string | undefined
  >(grouping ? getCollaboratorColor(grouping) : undefined);

  /**
   * Register the component on mount.
   * This puts the state updater (setIdentities) in the state
   * to expose it to the cell presence updates.
   */
  const updater = useCallback(
    (newParticipants: Participant[]) => {
      if (!isEnabled) {
        return;
      }

      setParticipants(newParticipants);

      if (grouping) {
        const newCollabColor = presenceState.getRecentCollaboratorColor(grouping);

        setRecentCollaboratorColor(newCollabColor);
      }
    },
    [grouping, isEnabled]
  );

  useEffect(() => {
    if (grouping) {
      const newParticipants = getParticipants(grouping);

      if (!areArraysShallowEqual(participants, newParticipants)) {
        setParticipants(newParticipants);
      }
    }
    // We don't want to re-run this when participants change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getParticipants, grouping]);

  useEffect(() => {
    if (grouping) {
      const newColor = getCollaboratorColor(grouping);

      setRecentCollaboratorColor((prev) => {
        if (prev !== newColor) {
          return newColor;
        }

        return prev;
      });
    }
  }, [getCollaboratorColor, grouping]);

  useEffect(() => {
    if (!grouping) {
      return;
    }

    let unsubscribe: VoidFunction | undefined;

    if (isCellGrouping(grouping)) {
      unsubscribe = subscribeTo(key('cell', grouping), 'presence', updater);
    } else if (isColumnGrouping(grouping)) {
      unsubscribe = subscribeTo(key('column', grouping), 'presence', updater);
    } else if (isRowGrouping(grouping)) {
      unsubscribe = subscribeTo(key('row', grouping), 'presence', updater);
    }

    return () => {
      unsubscribe?.();
    };
  }, [componentId, grouping, updater]);

  return {
    participants,
    recentCollaboratorColor
  };
};
